import { Box, Stack, styled, Switch } from '@ethings/ui-toolkit';
import { Divider, Typography } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { T_MANY } from '../../constants';
import { BaseLayers, ToggleLayers } from '../../types';
import { SubSectionData } from '../../types/map';
import { useMapLayers } from '../map/hooks';
import { SubMenuSectionController } from '../map/layer-selection/sub-menu/sub-menu-section-controller';
import { getSubSectionData } from '../map/layer-selection/sub-menu/sub-menu.util';
import { getBaseLayerImage, mapTemplateTypeToToggleLayer } from './layer-mapping-new';
import { DetailLayerDefinition, EntityLayerDefinition, LayerMenuContentProps } from './layer-menu.types';
import { incidentsThumbnail, thingsThumbnail, trafficThumbnail, transportThumbnail } from './layer-thumbnails';
import { MapLayerToggle } from './map-layer-toggle';

const LAYER_GRID_GAP = 20;
const SUBMENU_MAX_WIDTH = 300;
const SUBMENU_PADDING_LEFT = 2;

interface EntityLayerSectionProps {
  layer: { type: string; label: string };
  isEnabled: boolean;
  subsections: SubSectionData[];
  onChange: (type: string, checked: boolean) => void;
  testId?: string;
  disabled?: boolean;
}

const SectionHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const SubMenuContainer = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(SUBMENU_PADDING_LEFT),
  marginTop: theme.spacing(1),
  maxWidth: SUBMENU_MAX_WIDTH,
  '& .MuiFormControlLabel-root': {
    maxWidth: '100%',
  },
}));

const LayerGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: LAYER_GRID_GAP,
}));

const EntityLayerSection: FC<EntityLayerSectionProps> = ({ layer, isEnabled, subsections, onChange, testId, disabled = false }) => {
  const { t } = useTranslation(['common']);

  return (
    <Box>
      <SectionHeader>
        <Typography variant="body1" fontWeight="bold">{t(layer.label)}</Typography>
        <Switch checked={isEnabled} disabled={disabled} onChange={(e) => onChange(layer.type, e.target.checked)} data-testid={testId} />
      </SectionHeader>

      {subsections.length > 0 && (
        <SubMenuContainer>
          {subsections.map(({ cacheDataTypes, cacheFilter, itemType, storageKey, typePropertyName }) => (
            <SubMenuSectionController
              key={itemType}
              cacheDataTypes={cacheDataTypes}
              cacheFilter={cacheFilter}
              disabled={!isEnabled}
              label={t('common:component.filter-dropdown.labels.select-all')}
              storageKey={storageKey}
              typePropertyName={typePropertyName}
            />
          ))}
        </SubMenuContainer>
      )}
    </Box>
  );
};

export const LayerMenuContent: FC<LayerMenuContentProps> = ({
  baseLayers,
  disabledLayers = [],
  detailLayers,
  entityLayers,
  handleBaseChange,
  handleDetailLayerChange,
  handleEntityLayerChange,
  isDetailLayerChecked,
  isEntityLayerChecked,
  selectedBaseLayer,
  zoomLevel,
  mapWidth,
  mapHeight,
}) => {
  const { t } = useTranslation(['common', 'track']);
  const { displayThingsLayerSelection, isThingsLayerSelected, setIsThingsLayerSelected } = useMapLayers();

  const geofencesLayer = entityLayers.find((layer) => layer.type === 'geofences');
  const poiLayer = entityLayers.find((layer) => layer.type === 'point-of-interest');

  const geofenceSubsections = geofencesLayer ? getSubSectionData(ToggleLayers.geofences) : [];
  const poiSubsections = poiLayer ? getSubSectionData(ToggleLayers.pointOfInterest) : [];

  const isGeofencesEnabled = geofencesLayer ? isEntityLayerChecked(geofencesLayer.type) : false;
  const isPOIEnabled = poiLayer ? isEntityLayerChecked(poiLayer.type) : false;

  const isLayerDisabled = (layer: DetailLayerDefinition | EntityLayerDefinition): boolean => {
    const toggleLayer = mapTemplateTypeToToggleLayer(layer.type);
    if (toggleLayer && disabledLayers.includes(toggleLayer)) {
      return true;
    }
    if (layer.minZoomLevel && zoomLevel < layer.minZoomLevel) {
      return true;
    }
    if (layer.maxBoundingBoxSize && (mapWidth > layer.maxBoundingBoxSize || mapHeight > layer.maxBoundingBoxSize)) {
      return true;
    }
    return false;
  };

  const getDetailLayerThumbnail = (type: string): string => {
    const thumbnails: Record<string, string> = {
      things: thingsThumbnail,
      traffic: trafficThumbnail,
      incidents: incidentsThumbnail,
      transport: transportThumbnail,
    };
    return thumbnails[type] || thingsThumbnail;
  };

  return (
    <Box pb={2}>
      {!!baseLayers.length && (
        <Box mb={2}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
            {t('common:component.map.layers.labels.layers')}
          </Typography>
          <LayerGrid>
            {baseLayers.map((layer) => {
              const image = getBaseLayerImage(layer.type);
              const isSelected = selectedBaseLayer === layer.type;
              return (
                <MapLayerToggle
                  key={layer.type}
                  type={layer.type}
                  label={t(layer.label)}
                  thumbnail={image || <Box width="100%" height="100%" bgcolor="grey.300" />}
                  checked={isSelected}
                  onChange={(type) => handleBaseChange(type as BaseLayers)}
                  data-testid={layer.testId}
                />
              );
            })}
          </LayerGrid>
        </Box>
      )}
      <Divider sx={{ my: 2 }} />
      <Box mb={2}>
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>{t('common:component.map.layers.labels.details')}</Typography>
        <LayerGrid>
          {displayThingsLayerSelection && detailLayers.some((layer) => layer.type === 'things') && (
            <MapLayerToggle
              type="things"
              label={t('terms:thing', { count: T_MANY })}
              thumbnail={getDetailLayerThumbnail('things')}
              checked={isThingsLayerSelected}
              onChange={() => setIsThingsLayerSelected(!isThingsLayerSelected)}
              data-testid="layer-select-things"
            />
          )}

          {detailLayers.filter((layer) => layer.type !== 'things').map((layer) => {
            const isSelected = isDetailLayerChecked(layer.type);
            const disabled = isLayerDisabled(layer);

            return (
              <MapLayerToggle
                key={layer.type}
                type={layer.type}
                label={t(layer.label)}
                thumbnail={getDetailLayerThumbnail(layer.type)}
                checked={isSelected}
                disabled={disabled}
                tooltip={disabled ? t('track:component.map.layers.labels.zoom-warning') : undefined}
                onChange={(type) => handleDetailLayerChange(type)}
                data-testid={layer.testId}
              />
            );
          })}
        </LayerGrid>
      </Box>
      <Divider sx={{ my: 2 }} />
      {geofencesLayer && (
        <EntityLayerSection
          layer={geofencesLayer}
          isEnabled={isGeofencesEnabled}
          subsections={geofenceSubsections}
          onChange={handleEntityLayerChange}
          testId="geofences-switch"
          disabled={isLayerDisabled(geofencesLayer)}
        />
      )}
      <Divider sx={{ my: 2 }} />
      {poiLayer && (
        <EntityLayerSection
          layer={poiLayer}
          isEnabled={isPOIEnabled}
          subsections={poiSubsections}
          onChange={handleEntityLayerChange}
          testId="poi-switch"
          disabled={isLayerDisabled(poiLayer)}
        />
      )}
    </Box>
  );
};
